import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import MediaRenderer, { resolveItemMedia } from './MediaRenderer';

export default function AreasSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const mediaMode = config.media_mode || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const carouselInterval = (config.carousel_interval || 4) * 1000;
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const items = content.items || [];
    const variant = config.variant || 'tabs-horizontal';
    const accent = config.accent_color || primaryColor;
    const [activeTab, setActiveTab] = useState(0);

    if (items.length === 0) return null;

    const sectionCls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const activeItem = items[activeTab] || items[0];
    const features = (activeItem.features || '').split('\n').filter(Boolean);

    // Resolve media for the active item
    const activeResolved = resolveItemMedia(activeItem || {}, mediaMode, mediaFolders);

    // Tabs vertical variant
    if (variant === 'tabs-vertical') {
        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {/* Left tabs */}
                        <div className="lg:col-span-3 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible">
                            {items.map((item, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTab(i)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all whitespace-nowrap ${
                                        activeTab === i
                                            ? 'shadow-md'
                                            : s.dark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                                    }`}
                                    style={activeTab === i ? { backgroundColor: hexToRgba(accent, 0.08), color: accent } : { color: s.textSub }}
                                >
                                    {item.icon && <span className="text-lg shrink-0">{item.icon}</span>}
                                    <span className="text-sm font-semibold">{item.title}</span>
                                </button>
                            ))}
                        </div>
                        {/* Right content */}
                        <div className="lg:col-span-9">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                                {/* Info */}
                                <div>
                                    <div className="flex items-center gap-3 mb-4">
                                        {activeItem.icon && <span className="text-3xl">{activeItem.icon}</span>}
                                        <h3 className="text-2xl font-bold" style={{ color: s.text }}>{activeItem.title}</h3>
                                    </div>
                                    {activeItem.description && (
                                        <p className="text-sm leading-relaxed mb-6" style={{ color: s.textSub }}>{activeItem.description}</p>
                                    )}
                                    <MediaRenderer
                                        item={{ ...activeItem, ...activeResolved }}
                                        fitClass={fitClass}
                                        interval={carouselInterval}
                                        accent={accent}
                                        aspect="aspect-[4/3]"
                                        rounded="rounded-xl"
                                        className="w-full shadow-lg"
                                    />
                                </div>
                                {/* Features checklist */}
                                {features.length > 0 && (
                                    <div className="space-y-3">
                                        <h4 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: accent }}>Caracteristicas</h4>
                                        <div className="grid grid-cols-1 gap-2.5">
                                            {features.map((feat, fi) => (
                                                <div key={fi} className="flex items-start gap-3">
                                                    <svg className="w-5 h-5 shrink-0 mt-0.5" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    <span className="text-sm" style={{ color: s.text }}>{feat}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
            </section>
        );
    }

    // Tabs horizontal variant (default)
    return (
        <section className={sectionCls} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}

                {/* Tabs */}
                <div className="flex flex-wrap justify-center gap-2 mb-10">
                    {items.map((item, i) => (
                        <button
                            key={i}
                            onClick={() => setActiveTab(i)}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                                activeTab === i
                                    ? 'text-white shadow-lg'
                                    : s.dark ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                            }`}
                            style={activeTab === i ? { backgroundColor: accent } : {}}
                        >
                            {item.icon && <span>{item.icon}</span>}
                            {item.title}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                    {/* Left side -- image + description */}
                    <div>
                        <div className="mb-6">
                            <MediaRenderer
                                item={{ ...activeItem, ...activeResolved }}
                                fitClass={fitClass}
                                interval={carouselInterval}
                                accent={accent}
                                aspect="aspect-[4/3]"
                                rounded="rounded-2xl"
                                className="w-full shadow-lg"
                            />
                        </div>
                        <h3 className="text-2xl font-bold mb-3" style={{ color: s.text }}>{activeItem.title}</h3>
                        {activeItem.description && (
                            <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{activeItem.description}</p>
                        )}
                    </div>
                    {/* Right side -- features checklist in 2 columns */}
                    {features.length > 0 && (
                        <div>
                            <h4 className="text-sm font-bold uppercase tracking-wider mb-6" style={{ color: accent }}>Caracteristicas principales</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {features.map((feat, fi) => (
                                    <div key={fi} className="flex items-start gap-3">
                                        <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: hexToRgba(accent, 0.08) }}>
                                            <svg className="w-3 h-3" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <span className="text-sm" style={{ color: s.text }}>{feat}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
        </section>
    );
}
